import { startOfDay } from 'date-fns';

import type { UserNotification } from '@jwp/ott-common/types/notification';

export type NotificationSection = {
  id: string;
  isToday: boolean;
  notifications: UserNotification[];
};

const sortByDateDesc = (notifications: UserNotification[]) =>
  [...notifications].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

const getDateKey = (createdAt: string) => new Date(createdAt).toLocaleDateString('en-CA');

export const groupNotifications = (notifications: UserNotification[]): NotificationSection[] => {
  const todayStart = startOfDay(new Date());
  const todayNotifications: UserNotification[] = [];
  const byDate = new Map<string, UserNotification[]>();

  sortByDateDesc(notifications).forEach((notification) => {
    const date = new Date(notification.createdAt);

    if (date >= todayStart) {
      todayNotifications.push(notification);
      return;
    }

    const dateKey = getDateKey(notification.createdAt);
    const existing = byDate.get(dateKey) ?? [];
    existing.push(notification);
    byDate.set(dateKey, existing);
  });

  const sections: NotificationSection[] = [];

  if (todayNotifications.length > 0) {
    sections.push({ id: 'today', isToday: true, notifications: todayNotifications });
  }

  Array.from(byDate.entries())
    .sort(([a], [b]) => b.localeCompare(a))
    .forEach(([dateKey, items]) => {
      sections.push({ id: dateKey, isToday: false, notifications: items });
    });

  return sections;
};

export const formatNotificationDateHeader = (dateKey: string, locale: string): string => {
  const date = new Date(`${dateKey}T00:00:00`);
  return date.toLocaleDateString(locale, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
};

export const formatNotificationTime = (createdAt: string): string => {
  const date = new Date(createdAt);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMinutes < 1) { return 'Just now'; }
  if (diffMinutes < 60) { return `${diffMinutes} minute${diffMinutes === 1 ? '' : 's'} ago`; }
  if (diffHours < 24) { return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`; }
  if (diffDays === 1) { return '1 day ago'; }
  return `${diffDays} days ago`;
};
